import { MaterialSymbol, PartnerOnboardingApplication, parseUTCDateTime, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { BodyText, Button, Card, CircleButton, Title, TransactionStatus, UtilButton } from 'UI';
import usePartnerAccountStatusLabel from '@/hooks/usePartnerAccountStatusLabel';
import useEntityTypeLabels from '@/components/OnboardingTracker/useEntityTypeLabels';
// Figma's "Contact Icon" (blue gradient, building glyph) is the account page's existing Active icon.
import EntityIcon from '@/components/Accounts/Account/svg/Active.svg';
import { isPartnerOnboardingStatus } from '../types';
import { ApplicationLink, formatDisplayDate, getDaysLeft } from './applicationDetails';
import styles from './IdentityCard.module.scss';

const cn = withStyles(styles);

const NO_VALUE = '—';

type IdentityCardProps = {
  application: PartnerOnboardingApplication;
  /** `null` when the entity has no onboarding application: only the identity block is shown. */
  link: ApplicationLink | null;
  isReadOnly: boolean;
  canResendEmail: boolean;
  /** An action is in flight: every action control is disabled until it settles. */
  isActionPending: boolean;
  onExtendClick: () => void;
  onOpenClick: () => void;
  onCopyClick: () => void;
  onResendClick: () => void;
  onSendReminderClick: () => void;
};

export default function IdentityCard({
  application,
  link,
  isReadOnly,
  canResendEmail,
  isActionPending,
  onExtendClick,
  onOpenClick,
  onCopyClick,
  onResendClick,
  onSendReminderClick,
}: IdentityCardProps) {
  const { t, i18n } = useTranslation('translations', {
    keyPrefix: 'OnboardingTracker.ApplicationDetailsDrawer.IdentityCard',
  });

  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const { resolveEntityTypeLabel } = useEntityTypeLabels();

  const isActionDisabled = isReadOnly || isActionPending;
  const entityTypeLabel = resolveEntityTypeLabel(application.EntityType);
  const lastActivity = application.LastActivity ? parseUTCDateTime(application.LastActivity) : null;

  return (
    <Card variant="section" className={cn('card')} data-testid="identityCard">
      <div className={cn('identity')}>
        {/* Judgement call (Figma shows only an Account): one icon for all three entity types. */}
        <img className={cn('entityIcon')} src={EntityIcon} alt="" />

        <div className={cn('name')}>
          <Title level="3">{application.Name}</Title>

          {/* Judgement call (spec §6.5 assumes a date): `LastActivity` is null until the applicant first
              opens the application, and "Last activity" with no date says nothing, so only the type shows. */}
          <BodyText className={cn('secondary')}>
            {lastActivity
              ? t('meta', {
                  defaultValue: '{{entityType}} · Last activity {{date}}',
                  entityType: entityTypeLabel,
                  date: formatDisplayDate(lastActivity, i18n.language),
                })
              : entityTypeLabel}
          </BodyText>
        </div>

        <div className={cn('status')}>
          {/* Track Progress opens the drawer from Application Pending rows only (ID-14136), but the status is
              the drawer's own read, which can have moved on since the list loaded. Onboarding stages get
              Figma's grey pending chip, as the table does; Active gets the app's green active chip. */}
          <TransactionStatus
            status={getStatusChipVariant(application.Status)}
            label={resolveStatusLabel(application.Status)}
          />
        </div>
      </div>

      {link && (
        <div className={cn('tiles')}>
          <div className={cn('tile', 'expiryTile')}>
            <div className={cn('tileHeader')} data-testid="tileHeader">
              <BodyText size="sm" weight="md">
                {t('expiryDate', { defaultValue: 'Expiry Date' })}
              </BodyText>

              {/* Figma "Link Button" (Body/13/Medium, blue) is UtilButton's small size. Judgement call (specs
                  silent): disabled, not hidden, on a Submitted link, because `/extend` rejects a submitted
                  application; disabling keeps the tile's layout the same as for a Viewer. */}
              <UtilButton
                size="small"
                disabled={isActionDisabled || link.linkStatus === 'Submitted'}
                onClick={onExtendClick}
              >
                {t('extend', { defaultValue: 'Extend' })}
              </UtilButton>
            </div>

            <div className={cn('expiry')}>
              <Title level="4">{formatDisplayDate(link.expiryDate, i18n.language)}</Title>
              <span className={cn('caption')}>{renderExpiryCaption()}</span>
            </div>
          </div>

          <div className={cn('tile', 'linkTile')}>
            <div className={cn('tileHeader')} data-testid="tileHeader">
              <BodyText size="sm" weight="md">
                {t('onboardingLink', { defaultValue: 'Onboarding Link' })}
              </BodyText>

              {renderLinkStatusTag()}
            </div>

            <div className={cn('linkRow')}>
              <BodyText className={cn('secondary', 'url')}>{link.onboardingURL ?? NO_VALUE}</BodyText>

              {/* Spec §6.9: opens the application without OTP, with a link fetched on click. `/open` mints its
                  own link, but it is disabled with the copy icon when the card has no link: no link on the
                  card means none to open either. */}
              <CircleButton
                icon="open_in_new"
                size="large"
                colorScheme="action"
                circleFill={false}
                aria-label={t('openApplication', { defaultValue: 'Open the application' })}
                disabled={isActionDisabled || !link.onboardingURL}
                onClick={onOpenClick}
              />

              {/* Spec §6.9: copies the normal, OTP-gated link. Disabled for view-only, as on the account page. */}
              <CircleButton
                icon="content_copy"
                size="large"
                colorScheme="action"
                circleFill={false}
                aria-label={t('copyLink', { defaultValue: 'Copy the onboarding link' })}
                disabled={isReadOnly || !link.onboardingURL}
                onClick={onCopyClick}
              />
            </div>

            <div className={cn('actions')}>
              {canResendEmail && (
                <Button size="small" className={cn('resend')} disabled={isActionDisabled} onClick={onResendClick}>
                  {t('resend', { defaultValue: 'Resend' })}
                </Button>
              )}

              <Button colorScheme="primary" size="small" disabled={isActionDisabled} onClick={onSendReminderClick}>
                {t('sendReminder', { defaultValue: 'Send Reminder' })}
              </Button>
            </div>

            {/* Figma is silent; today's tracker shows the last reminder, so it sits under the buttons that
                send one, in the same caption style as the expiry line. */}
            {link.lastReminderSentDate && (
              <span className={cn('caption')}>
                {t('lastReminder', {
                  defaultValue: 'Last reminder: {{date}}',
                  date: formatDisplayDate(link.lastReminderSentDate, i18n.language, { isOrdinal: true }),
                })}
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );

  function renderExpiryCaption() {
    if (!link) return null;

    const sentOn = formatDisplayDate(link.sentDate, i18n.language, { isOrdinal: true });

    // Judgement call (specs silent): the applicant has nothing left to do before the expiry, so a countdown
    // would mislead; the caption names the state instead, as the expired one does.
    if (link.linkStatus === 'Submitted') {
      return t('submittedSentOn', { defaultValue: 'Submitted · Sent on {{date}}', date: sentOn });
    }

    // Judgement call (specs silent): a negative "days left" reads as a bug, so an expired link says so.
    if (link.linkStatus === 'Expired' || getDaysLeft(link.expiryDate) < 0) {
      return t('expiredSentOn', { defaultValue: 'Expired · Sent on {{date}}', date: sentOn });
    }

    return t('daysLeftSentOn', {
      defaultValue: '{{count}} days left · Sent on {{date}}',
      count: getDaysLeft(link.expiryDate),
      date: sentOn,
    });
  }

  function renderLinkStatusTag() {
    if (!link) return null;

    // Spec §6.5 names only "Form Active" (green, Figma's check_circle tag). Specs are silent on the other
    // two: "Inactive" is today's iframe label for an expired link, and "Submitted" replaces the iframe's
    // "Complete", which reads oddly next to a link. Their icons and colours are judgement calls.
    switch (link.linkStatus) {
      case 'Active':
        return (
          <span className={cn('tag', 'tagActive')}>
            <MaterialSymbol icon="check_circle" size="16px" fillAmount={1} />
            {t('LinkStatus.active', { defaultValue: 'Form Active' })}
          </span>
        );
      case 'Expired':
        return (
          <span className={cn('tag', 'tagInactive')}>
            <MaterialSymbol icon="cancel" size="16px" fillAmount={1} />
            {t('LinkStatus.inactive', { defaultValue: 'Inactive' })}
          </span>
        );
      case 'Submitted':
        return (
          <span className={cn('tag', 'tagSubmitted')}>
            <MaterialSymbol icon="task_alt" size="16px" />
            {t('LinkStatus.submitted', { defaultValue: 'Submitted' })}
          </span>
        );
    }
  }
}

function getStatusChipVariant(status: PartnerOnboardingApplication['Status']) {
  if (isPartnerOnboardingStatus(status)) return 'pending';

  return status === 'Active' ? 'active' : 'neutral';
}
